/**
 * View Mode Configurations
 * 
 * Modular view mode definitions that combine container strategies with CSS layouts.
 */

import type { ViewModeConfig, ContainerStrategy, GraphNode } from './types.js';
import {
  CalendarContainerStrategy,
  GridContainerStrategy,
  CarouselContainerStrategy,
  StackContainerStrategy,
  NoContainerStrategy
} from './container-strategies.js';

/**
 * Graph container strategy (no containers, just content nodes with relationships)
 */
class GraphContainerStrategy implements ContainerStrategy {
  generateContainers(count: number, config: Record<string, unknown>): GraphNode[] {
    return []; // Graph view doesn't use containers
  }

  getLayoutCSS(): string {
    return `
      position: relative;
      width: 100%;
      height: 100%;
    `;
  }
}

/**
 * Get view mode configuration
 */
export function getViewModeConfig(
  viewMode: ViewModeConfig['viewMode'],
  containerCount?: number,
  containerConfig?: Record<string, unknown>
): ViewModeConfig {
  let containerStrategy: ContainerStrategy;
  let cssLayout: string;

  switch (viewMode) {
    case 'calendar':
      containerStrategy = new CalendarContainerStrategy();
      containerCount = containerCount || 7; // Default to week view
      cssLayout = containerStrategy.getLayoutCSS();
      break;

    case 'grid':
      containerStrategy = new GridContainerStrategy();
      containerCount = containerCount || 12; // Default to 12 items
      cssLayout = containerStrategy.getLayoutCSS();
      break;

    case 'carousel':
      containerStrategy = new CarouselContainerStrategy();
      containerCount = containerCount || 5; // Default to 5 slides
      cssLayout = containerStrategy.getLayoutCSS();
      break;

    case 'stack':
      containerStrategy = new StackContainerStrategy();
      containerCount = containerCount || 5; // Default to 5 cards
      cssLayout = containerStrategy.getLayoutCSS();
      break;

    case 'graph':
      containerStrategy = new GraphContainerStrategy();
      cssLayout = containerStrategy.getLayoutCSS();
      break;

    case 'list':
    default:
      containerStrategy = new NoContainerStrategy();
      cssLayout = containerStrategy.getLayoutCSS();
      break;
  }

  return {
    viewMode,
    containerStrategy,
    containerCount,
    containerConfig: containerConfig || {},
    cssLayout
  };
}

